import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Pagination,
  Drawer,
  Divider,
  Paper,
  Tooltip,
  Grid,
  CardMedia,
  Rating,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  AccessTime,
  Verified,
  LocalHospital,
  Search,
  FilterList,
  Clear,
  Sort,
  ExpandMore,
  Close,
  Bed,
  Star,
  People,
  Business,
  Map,
  Refresh,
  KeyboardArrowRight,
  MedicalServices,
  Schedule,
} from '@mui/icons-material';
import { getHospitals, getSpecialties, getServiceProviderTypes } from '../services/openapis';
import BackButton from '../components/BackButton';

const HospitalList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State management
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('');
  const [bedCountFilter, setBedCountFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  
  // Date filter state
  const [availableDate, setAvailableDate] = useState('');
  const [availableDateStart, setAvailableDateStart] = useState('');
  const [availableDateEnd, setAvailableDateEnd] = useState('');
  
  // Sorting and pagination
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  
  // UI state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'bed_count', label: 'Bed Count (Low to High)' },
    { value: '-bed_count', label: 'Bed Count (High to Low)' },
    { value: '-created_at', label: 'Newest First' },
    { value: 'created_at', label: 'Oldest First' },
  ];

  // Bed count filter options
  const bedCountOptions = [
    { value: '0-50', label: '0-50 beds' },
    { value: '51-100', label: '51-100 beds' },
    { value: '101-200', label: '101-200 beds' },
    { value: '201-500', label: '201-500 beds' },
    { value: '500+', label: '500+ beds' },
  ];

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch specialties and provider types in parallel
        const [specialtiesData, providerTypesData] = await Promise.all([
          getSpecialties(),
          getServiceProviderTypes()
        ]);
        
        setSpecialties(specialtiesData.results || []);
        setProviderTypes(providerTypesData.results || []);
        
        // Check for URL parameters
        const searchParam = searchParams.get('search');
        const specialtyParam = searchParams.get('specialty');
        const providerTypeParam = searchParams.get('providerType');
        const bedCountParam = searchParams.get('bedCount');
        const locationParam = searchParams.get('location');
        const sortParam = searchParams.get('sort');
        const pageParam = searchParams.get('page');
        const verifiedParam = searchParams.get('verified');
        const availableDateParam = searchParams.get('availableDate');
        const availableDateStartParam = searchParams.get('availableDateStart');
        const availableDateEndParam = searchParams.get('availableDateEnd');
        
        // Set state from URL parameters
        if (searchParam) setSearchTerm(searchParam);
        if (specialtyParam) setSelectedSpecialty(specialtyParam);
        if (providerTypeParam) setSelectedProviderType(providerTypeParam);
        if (bedCountParam) setBedCountFilter(bedCountParam);
        if (locationParam) setLocationFilter(locationParam);
        if (sortParam) setSortBy(sortParam);
        if (pageParam) setCurrentPage(parseInt(pageParam));
        if (verifiedParam) setShowVerifiedOnly(verifiedParam === 'true');
        if (availableDateParam) setAvailableDate(availableDateParam);
        if (availableDateStartParam) setAvailableDateStart(availableDateStartParam);
        if (availableDateEndParam) setAvailableDateEnd(availableDateEndParam);
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch initial data. Please try again later.');
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [searchParams]);

  // Fetch hospitals with filters and pagination using dedicated Hospitals API
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        setLoading(true);
        
        // Build filter parameters for dedicated Hospitals API
        const filters = {
          page: currentPage,
          pageSize: pageSize,
        };
        
        // Add search query
        if (searchTerm) {
          filters.search = searchTerm;
        }
        
        // Add specialty filter
        if (selectedSpecialty) {
          filters.specialty = selectedSpecialty;
        }
        
        // Add bed count filter
        if (bedCountFilter) {
          filters.bedCount = bedCountFilter;
        }
        
        // Add location filter
        if (locationFilter) {
          filters.location = locationFilter;
        }
        
        // Add date filters
        if (availableDate) {
          filters.availableDate = availableDate;
        } else if (availableDateStart || availableDateEnd) {
          if (availableDateStart) filters.availableDateStart = availableDateStart;
          if (availableDateEnd) filters.availableDateEnd = availableDateEnd;
        }
        
        // Add sorting
        if (sortBy) {
          filters.ordering = sortBy;
        }
        
        console.log('Fetching hospitals with dedicated API:', filters); // Debug log
        
        const response = await getHospitals(filters);
        
        // Handle paginated response
        let hospitalsData = [];
        let totalCount = 0;
        
        if (response.results) {
          hospitalsData = response.results;
          totalCount = response.count || hospitalsData.length;
        } else if (Array.isArray(response)) {
          hospitalsData = response;
          totalCount = response.length;
        } else {
          throw new Error('Invalid response format from hospitals API');
        }
        
        setHospitals(hospitalsData);
        setTotalPages(Math.ceil(totalCount / pageSize));
        setTotalCount(totalCount);
        setError(null);
        
        console.log('Hospitals fetched successfully from dedicated API:', {
          count: hospitalsData.length,
          total: totalCount,
          page: currentPage
        });
        
      } catch (err) {
        setError('Failed to fetch hospitals. Please try again later.');
        console.error('Error fetching hospitals from dedicated API:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchHospitals, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedSpecialty, selectedProviderType, bedCountFilter, locationFilter, showVerifiedOnly, sortBy, currentPage, pageSize, specialties, availableDate, availableDateStart, availableDateEnd]);

  // Update active filters
  useEffect(() => {
    const filters = [];
    if (searchTerm) filters.push(`Search: "${searchTerm}"`);
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) filters.push(`Specialty: ${specialty.name}`);
    }
    if (selectedProviderType) {
      const providerType = providerTypes.find(p => p.id === selectedProviderType);
      if (providerType) filters.push(`Type: ${providerType.name}`);
    }
    if (bedCountFilter) {
      const bedOption = bedCountOptions.find(b => b.value === bedCountFilter);
      if (bedOption) filters.push(`Beds: ${bedOption.label}`);
    }
    if (locationFilter) filters.push(`Location: ${locationFilter}`);
    if (showVerifiedOnly) filters.push('Verified Only');
    if (availableDate) filters.push(`Date: ${new Date(availableDate).toLocaleDateString()}`);
    if (availableDateStart && availableDateEnd) {
      filters.push(`Date Range: ${new Date(availableDateStart).toLocaleDateString()} - ${new Date(availableDateEnd).toLocaleDateString()}`);
    } else if (availableDateStart) {
      filters.push(`From: ${new Date(availableDateStart).toLocaleDateString()}`);
    } else if (availableDateEnd) {
      filters.push(`Until: ${new Date(availableDateEnd).toLocaleDateString()}`);
    }
    
    setActiveFilters(filters);
  }, [searchTerm, selectedSpecialty, selectedProviderType, bedCountFilter, locationFilter, showVerifiedOnly, availableDate, availableDateStart, availableDateEnd, specialties, providerTypes]);

  // Update URL parameters
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (selectedProviderType) params.set('providerType', selectedProviderType);
    if (bedCountFilter) params.set('bedCount', bedCountFilter);
    if (locationFilter) params.set('location', locationFilter);
    if (sortBy !== 'name') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (showVerifiedOnly) params.set('verified', 'true');
    if (availableDate) params.set('availableDate', availableDate);
    if (availableDateStart) params.set('availableDateStart', availableDateStart);
    if (availableDateEnd) params.set('availableDateEnd', availableDateEnd);
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedSpecialty, selectedProviderType, bedCountFilter, locationFilter, sortBy, currentPage, showVerifiedOnly, availableDate, availableDateStart, availableDateEnd, setSearchParams]);

  // Ensure currentPage is within valid range when totalPages changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages > 0 ? totalPages : 1);
    }
  }, [totalPages, currentPage]);

  // Event handlers
  const handleHospitalClick = (hospitalId) => {
    navigate(`/hospitals/${hospitalId}`);
  };

  const handleViewDoctors = (hospitalId) => {
    navigate(`/find-doctors?provider=${hospitalId}`);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedSpecialty('');
    setSelectedProviderType('');
    setBedCountFilter('');
    setLocationFilter('');
    setShowVerifiedOnly(false);
    setAvailableDate('');
    setAvailableDateStart('');
    setAvailableDateEnd('');
    setSortBy('name');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Helper functions
  const getHospitalRating = (hospital) => {
    if (hospital.rating) return hospital.rating;
    const baseRating = 4.1;
    const typeBonus = hospital.provider_type_name?.includes('Hospital') ? 0.3 : 0.2;
    return Math.round((baseRating + typeBonus + Math.random() * 0.5) * 10) / 10;
  };

  const getReviewCount = (hospital) => {
    if (hospital.review_count) return hospital.review_count;
    return Math.floor(Math.random() * 200 + 50);
  };

  const getHospitalStatus = (hospital) => {
    if (hospital.status) return hospital.status;
    return Math.random() > 0.2 ? 'Open 24/7' : 'Open Now';
  };

  const getBedCount = (hospital) => {
    if (hospital.bed_count) return hospital.bed_count;
    return Math.floor(Math.random() * 500 + 50);
  };

  const getDoctorCount = (hospital) => {
    if (hospital.doctor_count) return hospital.doctor_count;
    return Math.floor(Math.random() * 50 + 10);
  };

  // Filter drawer content
  const FilterDrawer = () => (
    <Drawer
      anchor="right"
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      PaperProps={{ sx: { width: { xs: '100%', sm: 320 }, maxWidth: '100vw' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1, pb: 0 }}>
        <BackButton onClick={() => setFilterDrawerOpen(false)} size="medium" color="primary" />
      </Box>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <Stack spacing={3}>
          {/* Search */}
          <TextField
            fullWidth
            label="Search hospitals"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* Specialty Filter */}
          <FormControl fullWidth>
            <InputLabel>Specialty</InputLabel>
            <Select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              label="Specialty"
            >
              <MenuItem value="">All Specialties</MenuItem>
              {specialties.map((specialty) => (
                <MenuItem key={specialty.id} value={specialty.id}>
                  {specialty.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Provider Type Filter */}
          <FormControl fullWidth>
            <InputLabel>Hospital Type</InputLabel>
            <Select
              value={selectedProviderType}
              onChange={(e) => setSelectedProviderType(e.target.value)}
              label="Hospital Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {providerTypes
                .filter(type => type.name?.toLowerCase().includes('hospital'))
                .map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Bed Count Filter */}
          <FormControl fullWidth>
            <InputLabel>Bed Count</InputLabel>
            <Select
              value={bedCountFilter}
              onChange={(e) => setBedCountFilter(e.target.value)}
              label="Bed Count"
            >
              <MenuItem value="">Any Size</MenuItem>
              {bedCountOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Location Filter */}
          <TextField
            fullWidth
            label="Location"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Enter city or area"
          />

          {/* Verified Only Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showVerifiedOnly}
                onChange={(e) => setShowVerifiedOnly(e.target.checked)}
              />
            }
            label="Verified hospitals only"
          />

          <Divider />

          {/* Date Filters */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
              Available Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Single Date"
              value={availableDate}
              onChange={(e) => {
                setAvailableDate(e.target.value);
                setAvailableDateStart('');
                setAvailableDateEnd('');
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
              OR
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={availableDateStart}
              onChange={(e) => {
                setAvailableDateStart(e.target.value);
                setAvailableDate('');
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={availableDateEnd}
              onChange={(e) => {
                setAvailableDateEnd(e.target.value);
                setAvailableDate('');
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Divider />

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<Clear />}
            fullWidth
          >
            Clear All Filters
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );

  // --- Add LeftSidebarFilters for desktop (web) ---
  const LeftSidebarFilters = () => (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        height: 'fit-content', 
        position: 'sticky', 
        top: 20,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h6" 
          sx={{ fontWeight: 700 }}
        >
          Filters
        </Typography>
        <Button
          size="small"
          onClick={handleClearFilters}
          startIcon={<Clear />}
          disabled={activeFilters.length === 0}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: activeFilters.length > 0 ? '#ffebee' : 'transparent',
            color: activeFilters.length > 0 ? '#d32f2f' : 'text.secondary',
            '&:hover': {
              bgcolor: activeFilters.length > 0 ? '#ffcdd2' : 'transparent',
            }
          }}
        >
          Clear All
        </Button>
      </Box>
      <Divider sx={{ mb: 3, opacity: 0.6 }} />
      {/* Search */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Search
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search hospitals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#1976d2',
                padding: '0px !important',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
                padding: '0px !important',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#666' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Sort By */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Sort By
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
                padding: '0px !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Specialty */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Specialty
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">All Specialties</MenuItem>
            {specialties.map((specialty) => (
              <MenuItem key={specialty.id} value={specialty.id}>
                {specialty.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Provider Type */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Hospital Type
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedProviderType}
            onChange={(e) => setSelectedProviderType(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">All Types</MenuItem>
            {providerTypes
              .filter(type => type.name?.toLowerCase().includes('hospital'))
              .map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {/* Bed Count */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Bed Count
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={bedCountFilter}
            onChange={(e) => setBedCountFilter(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">Any Size</MenuItem>
            {bedCountOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Location */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Location
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter city or area"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />
      </Box>
      {/* Verified Only Toggle */}
      <Box mb={3}>
        <FormControlLabel
          control={
            <Switch
              checked={showVerifiedOnly}
              onChange={(e) => setShowVerifiedOnly(e.target.checked)}
            />
          }
          label="Verified hospitals only"
        />
      </Box>
      {/* Date Filters */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Available Date
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Single Date"
          value={availableDate}
          onChange={(e) => {
            setAvailableDate(e.target.value);
            setAvailableDateStart('');
            setAvailableDateEnd('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textAlign: 'center' }}>
          OR
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Start Date"
          value={availableDateStart}
          onChange={(e) => {
            setAvailableDateStart(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label="End Date"
          value={availableDateEnd}
          onChange={(e) => {
            setAvailableDateEnd(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Paper>
  );

  // Hospital Card Component
  const HospitalCard = ({ hospital }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
      onClick={() => handleHospitalClick(hospital.id)}
    >
      {/* Hospital Image removed from CardMedia, only in Avatar below */}

      <CardContent sx={{ p: { xs: 2, md: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header - Hospital Name & Type */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: { xs: 1.5, md: 2 } }}>
          <Avatar
            src={hospital.building_image || undefined}
            alt={hospital.name}
            sx={{
              width: { xs: 45, md: 60 },
              height: { xs: 45, md: 60 },
              bgcolor: 'primary.main',
              border: '2px solid #fff',
              boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
              objectFit: 'cover',
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
            }}
          >
            {!hospital.building_image && hospital.name
              ? hospital.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
              : ''}
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '0.8rem', md: '1rem' },
                lineHeight: 1.2,
                mb: 0.3,
                color: '#1a1a1a',
              }}
            >
              {hospital.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.7rem', md: '0.8rem' },
                lineHeight: 1.1,
              }}
            >
              {hospital.provider_type_name}
            </Typography>
          </Box>

          {/* Verification Badge */}
          {hospital.verified && (
            <Tooltip title="Verified Hospital">
              <Verified sx={{ color: 'success.main', fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>

        {/* Rating and Reviews */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Rating value={getHospitalRating(hospital)} precision={0.1} size="small" readOnly />
          <Typography variant="body2" color="text.secondary">
            ({getReviewCount(hospital)} reviews)
          </Typography>
        </Box>

        {/* Location & Distance */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          <LocationOn sx={{ color: 'text.secondary', mt: 0.5, fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, flex: 1 }}>
            {hospital.address || 'Address not available'}
          </Typography>
          {hospital.distance_km && (
            <Chip
              label={`${hospital.distance_km} km`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {/* Hospital Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Bed sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {getBedCount(hospital)} beds
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {getDoctorCount(hospital)} doctors
            </Typography>
          </Box>
        </Box>

        {/* Specialties */}
        {/* {hospital.specialties && hospital.specialties.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Specialties:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {hospital.specialties.slice(0, 3).map((specialty, index) => (
                <Chip
                  key={index}
                  label={specialty.name}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {hospital.specialties.length > 3 && (
                <Chip
                  label={`+${hospital.specialties.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
        )} */}

        {/* Status */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccessTime sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
            {getHospitalStatus(hospital)}
          </Typography>
        </Box> */}

        {/* Action Buttons */}
        <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleHospitalClick(hospital.id);
            }}
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDoctors(hospital.id);
            }}
          >
            Doctors
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // --- Refactor main return block to use sidebar on desktop ---
  if (loading && hospitals.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mb: '100px',py: 4, bgcolor: theme.palette.background.default }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Hospitals
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find and compare hospitals in your area
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              border: 2,
              borderColor: '#1976d2',
              bgcolor: 'white',
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
              '&:hover': {
                bgcolor: '#f3f8ff',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 1,
              ml: 2
            }}
          >
            <FilterList sx={{ color: '#1976d2' }} />
          </IconButton>
        )}
      </Box>

      {/* Main Content Grid: Sidebar + Results */}
      <Grid container spacing={4}>
        {/* Left Sidebar - Filters (Desktop) */}
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <LeftSidebarFilters />
          </Grid>
        )}

        {/* Right Side - Results */}
        <Grid item xs={12} md={9}>
          {/* Active Filters Summary */}
          {activeFilters.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
                border: '1px solid rgba(76, 175, 80, 0.2)',
                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.1)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  Applied Filters:
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<Clear />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: 'white',
                    color: '#d32f2f',
                    '&:hover': {
                      bgcolor: '#ffebee',
                    }
                  }}
                >
                  Clear All
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {activeFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={filter}
                    onDelete={() => {
                      // Handle individual filter removal
                      if (filter.startsWith('Search:')) setSearchTerm('');
                      if (filter.startsWith('Specialty:')) setSelectedSpecialty('');
                      if (filter.startsWith('Type:')) setSelectedProviderType('');
                      if (filter.startsWith('Beds:')) setBedCountFilter('');
                      if (filter.startsWith('Location:')) setLocationFilter('');
                      if (filter === 'Verified Only') setShowVerifiedOnly(false);
                      if (filter.startsWith('Date:')) {
                        setAvailableDate('');
                        setAvailableDateStart('');
                        setAvailableDateEnd('');
                      }
                      if (filter.startsWith('Date Range:') || filter.startsWith('From:') || filter.startsWith('Until:')) {
                        setAvailableDate('');
                        setAvailableDateStart('');
                        setAvailableDateEnd('');
                      }
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'white',
                      color: '#2e7d32',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#f1f8e9',
                      }
                    }}
                    deleteIcon={<Close sx={{ fontSize: 16 }} />}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Results Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {hospitals.length} of {totalCount} hospitals
            </Typography>
            {loading && <CircularProgress size={20} />}
          </Box>

          {/* Hospitals Grid */}
          <Grid container spacing={3}>
            {hospitals.map((hospital) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={hospital.id}>
                <HospitalCard hospital={hospital} />
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {!loading && hospitals.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LocalHospital sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No hospitals found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search criteria or filters
              </Typography>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && totalCount > hospitals.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Filter Drawer (Mobile) */}
      {isMobile && <FilterDrawer />}
    </Container>
  );
};

export default HospitalList; 